/* eslint-disable react-hooks/exhaustive-deps */
import { Menu, MenuCategory, MenuSub } from '@/components/ui/typography/Typography';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDown, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function MobMenu({ menus, open, setOpen, pathname }) {
  const [active, setActive] = useState(null);
  const [subActive, setSubActive] = useState(null);

  // Reset submenu ketika parent ditutup
  // Tutup semua menu saat pindah halaman
  useEffect(() => {
    let foundParent = null;
    let foundGroup = null;

    menus.forEach((menu, i) => {
      // cek parent link
      if (menu.link === pathname) {
        foundParent = i;
      }

      // cek subMenu items
      menu.subMenu?.forEach((group, gi) => {
        group.items.forEach((item) => {
          if (item.link === pathname) {
            foundParent = i; // buka parent
            foundGroup = `${i}-${gi}`; // buka group
          }
        });
      });
    });

    setActive(foundParent);
    setSubActive(foundGroup);

    // jika ingin auto close panel ganti true → false
    // setOpen(true);
  }, [pathname]);

  // PANEL ANIMATION
  const panelVariant = {
    hidden: { x: '100%' },
    visible: {
      x: 0,
      transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
    },
    exit: {
      x: '100%',
      transition: { duration: 0.55, ease: [0.4, 0, 0.2, 1] },
    },
  };

  // DROPDOWN ANIMATION
  const dropdownVariant = {
    open: { height: 'auto', opacity: 1, transition: { duration: 0.45 } },
    closed: { height: 0, opacity: 0, transition: { duration: 0.3 } },
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-md z-[999]"
        >
          <motion.div
            variants={panelVariant}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="absolute right-0 top-0 h-full w-full bg-white/95 backdrop-blur-xl 
            text-gray-900 p-6 overflow-y-auto"
          >
            {/* =================HEADER================= */}
            <div className="flex justify-between items-center pb-6">
              <motion.img src="/logo/akr-logo-1.webp" className="h-6 w-auto" />
              <button
                onClick={() => setOpen(false)}
                className="p-2 rounded-full hover:bg-gray-200 transition"
              >
                <X size={24} />
              </button>
            </div>

            {/* =================MENU LIST================= */}
            <div className="space-y-3">
              {menus.map((menu, i) => {
                const hasSub = menu?.subMenu?.length > 0;

                // Cek Parent Utama
                const isActiveParent =
                  pathname === menu.link || pathname.startsWith(menu.link + '/');

                // Cek Submenu aktif
                const isSubPathActive = menu.subMenu?.some((group) =>
                  group.items.some(
                    (item) => pathname === item.link || pathname.startsWith(item.link + '/')
                  )
                );

                // FINAL STATUS PARENT
                const highlightParent = isActiveParent || isSubPathActive;

                return (
                  <div key={i}>
                    {/* =================PARENT================= */}
                    <div
                      className={`relative flex justify-between items-center py-3 cursor-pointer
                        font-semibold text-[15px] rounded-md px-1 transition
                        ${highlightParent ? 'text-purple-700 font-bold' : 'text-gray-900'}
                        hover:bg-gray-100/50`}
                      onClick={() => {
                        if (!hasSub) return;
                        setActive(active === i ? null : i);
                      }}
                    >
                      {!hasSub ? (
                        <Link
                          to={menu.link}
                          onClick={() => setOpen(false)}
                          className="block w-full relative"
                        >
                          <span className="relative inline-block">
                            <Menu>{menu.name}</Menu>

                            {/* underline kuning untuk menu tanpa submenu */}
                            <span
                              className={`absolute left-0 right-0 -bottom-[-2px] h-[10px] bg-yellow-300 rounded-sm
          -z-[1] origin-left transition-transform duration-300
          ${highlightParent ? 'scale-x-100' : 'scale-x-0'}`}
                            ></span>
                          </span>
                        </Link>
                      ) : (
                        <span className="relative z-10 inline-block">
                          <Menu>{menu.name}</Menu>
                          <span
                            className={`absolute left-0 right-0 -bottom-[2px] h-[10px] bg-yellow-300 rounded-sm
                              -z-[1] origin-left transition-transform duration-300
                              ${highlightParent ? 'scale-x-100' : 'scale-x-0'}`}
                          ></span>
                        </span>
                      )}

                      {hasSub && (
                        <motion.span
                          animate={{ rotate: active === i ? 180 : 0 }}
                          transition={{ duration: 0.35 }}
                          className="z-10"
                        >
                          <ChevronDown size={18} />
                        </motion.span>
                      )}
                    </div>

                    {/* =================SUB MENU================= */}
                    {hasSub && (
                      <motion.div
                        variants={dropdownVariant}
                        initial="closed"
                        animate={active === i ? 'open' : 'closed'}
                        className="pl-3 mt-2 overflow-hidden border-l-2 border-primary-200"
                      >
                        {menu.subMenu.map((group, gi) => {
                          const groupKey = `${i}-${gi}`;
                          const groupOpen = subActive === groupKey;

                          return (
                            <div key={gi} className="mb-2">
                              {/* SUB GROUP TITLE */}
                              <div
                                className={`flex justify-between items-center py-2 font-semibold text-[14px] cursor-pointer
                                  ${groupOpen ? 'text-purple-700' : 'text-gray-800'}
                                  hover:text-purple-700`}
                                onClick={() => setSubActive(groupOpen ? null : groupKey)}
                              >
                                <MenuCategory>{group.title}</MenuCategory>

                                <motion.span
                                  animate={{ rotate: groupOpen ? 180 : 0 }}
                                  transition={{ duration: 0.35 }}
                                >
                                  <ChevronDown size={15} />
                                </motion.span>
                              </div>

                              {/* ITEMS */}
                              <motion.div
                                variants={dropdownVariant}
                                initial="closed"
                                animate={groupOpen ? 'open' : 'closed'}
                                className="overflow-hidden"
                              >
                                {group.items.map((item, ii) => {
                                  const isActiveSub = pathname === item.link;

                                  return (
                                    <Link
                                      to={item.link}
                                      key={ii}
                                      onClick={() => setOpen(false)}
                                      className={`flex items-center gap-2 text-[14px] py-[6px] rounded-md transition px-2
                                        ${
                                          isActiveSub
                                            ? 'bg-purple-100 text-purple-700 font-semibold'
                                            : 'hover:bg-purple-50 hover:text-purple-700'
                                        }`}
                                    >
                                      <span
                                        className={`w-[6px] h-[6px] rounded-full 
                                          ${isActiveSub ? 'bg-purple-700' : 'bg-purple-500'}`}
                                      ></span>

                                      <MenuSub>{item.name}</MenuSub>
                                    </Link>
                                  );
                                })}
                              </motion.div>
                            </div>
                          );
                        })}
                      </motion.div>
                    )}
                  </div>
                );
              })}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
